// src/components/StripePaymentLink.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/StripePaymentPage.css';
import { createCheckoutSession, createPaymentAllocation, getEligibleUsers, getPaymentStatus } from '../services/api';

const StripePaymentLink = () => {
  const location = useLocation();
  const [customAmount, setCustomAmount] = useState('1000');
  const [checkoutError, setCheckoutError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTone, setStatusTone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activePlanId, setActivePlanId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [proxyUsers, setProxyUsers] = useState([]);
  const [proxyUsersError, setProxyUsersError] = useState('');
  const [selectedProxyUserId, setSelectedProxyUserId] = useState('');
  const [proxyError, setProxyError] = useState('');
  const [proxyMessage, setProxyMessage] = useState('');
  const [isAllocating, setIsAllocating] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const success = queryParams.get('success');
  const canceled = queryParams.get('canceled');

  const refreshPaymentStatus = useCallback(async () => {
    try {
      const data = await getPaymentStatus();
      setPaymentStatus(data);
      if (data?.is_paid) {
        setStatusTone('success');
        setStatusMessage('支払いが完了しました。');
      }
    } catch {
      setPaymentStatus(null);
    }
  }, []);

  useEffect(() => {
    refreshPaymentStatus();
  }, [refreshPaymentStatus]);

  useEffect(() => {
    let isMounted = true;
    const fetchUsers = async () => {
      setProxyUsersError('');
      try {
        const users = await getEligibleUsers();
        if (isMounted) {
          setProxyUsers(Array.isArray(users) ? users : []);
        }
      } catch {
        if (isMounted) {
          setProxyUsers([]);
          setProxyUsersError('ユーザー一覧の取得に失敗しました。');
        }
      }
    };
    fetchUsers();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!success && !canceled) {
      return;
    }

    if (canceled) {
      setStatusTone('error');
      setStatusMessage('支払いがキャンセルされました。');
      return;
    }

    refreshPaymentStatus();
  }, [success, canceled, refreshPaymentStatus]);

  // 各支援プランの情報
  const products = [
    {
      planId: 'support_500',
      price: '500円',
      description: '2025-2026年/21M',
      isCustom: false,
    },
    {
      planId: 'support_custom',
      price: '任意金額（上記プラン以外でご支援いただける方）',
      description:
        'ご自身で金額を指定いただきお支払いいただく方法です。\n' +
        '500円～100000円の間で任意に指定できます。\n' +
        '選択金額以外でご支援いただける金額がある場合はこちらをご使用ください。',
      isCustom: true,
    },
  ];

  const customAmountValue = Number(customAmount);
  const customAmountInvalid =
    Number.isNaN(customAmountValue) || customAmountValue < 500 || customAmountValue > 100000;

  const handleCheckout = async (product) => {
    setCheckoutError('');
    setStatusMessage('');
    setStatusTone('');
    setIsSubmitting(true);
    setActivePlanId(product.planId);

    const payload = { plan_id: product.planId };
    if (product.isCustom) {
      if (customAmountInvalid) {
        setCheckoutError('100円～100000円の範囲で入力してください。');
        setIsSubmitting(false);
        setActivePlanId(null);
        return;
      }
      payload.custom_amount = customAmountValue;
    }

    try {
      const response = await createCheckoutSession(payload);
      if (!response?.url) {
        throw new Error('Missing checkout url');
      }
      window.location.assign(response.url);
    } catch (error) {
      if (error?.response?.status === 401) {
        setCheckoutError('支払いにはログインが必要です。');
      } else {
        setCheckoutError('決済を開始できませんでした。もう一度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
      setActivePlanId(null);
    }
  };

  const availableUnits = paymentStatus?.available_units ?? 0;
  const handleProxyRegister = async () => {
    setProxyError('');
    setProxyMessage('');
    if (!selectedProxyUserId) {
      setProxyError('代理支払いするユーザーを選択してください。');
      return;
    }
    const selectedUser = proxyUsers.find((user) => String(user.id) === String(selectedProxyUserId));
    if (!selectedUser) {
      setProxyError('選択したユーザーが見つかりません。');
      return;
    }
    if (availableUnits <= 0) {
      setProxyError('代理支払いに使える枠がありません。');
      return;
    }
    const confirmed = window.confirm(
      `${selectedUser.username} を代理支払いとして登録します。よろしいですか？`
    );
    if (!confirmed) {
      return;
    }
    setIsAllocating(true);
    try {
      await createPaymentAllocation({ beneficiary_user_id: Number(selectedProxyUserId) });
      setProxyMessage('代理支払いの登録が完了しました。');
      setSelectedProxyUserId('');
      await refreshPaymentStatus();
    } catch (error) {
      const message = error?.response?.data?.detail;
      if (error?.response?.status === 401) {
        setProxyError('代理支払いの登録にはログインが必要です。');
      } else if (message) {
        setProxyError(message);
      } else {
        setProxyError('代理支払いの登録に失敗しました。');
      }
    } finally {
      setIsAllocating(false);
    }
  };

  return (
    <div className="page-container">
      <div className="container">
        <h2>ご支援プラン一覧</h2>
        <p>
          ご支援ありがとうございます。今年度・来年度の開発・維持費用として使用させていただきます。
          以下よりお選びください。
        </p>
        {statusMessage && (
          <div className={`payment-status ${statusTone}`}>
            {statusMessage}
          </div>
        )}
        {checkoutError && (
          <div className="payment-status error">
            {checkoutError}
          </div>
        )}
        {/* 利用者に各規約等への同意を促すセクション */}
        <div className="agreement-container">
          <p>
            お支払いを進める前に、以下の各リンク先の規約および情報をご確認いただき、
            内容に同意いただく必要がございます：
          </p>
          <ul className="legal-links">
            <li>
              <Link to="/legal/terms-of-service" target="_blank" rel="noopener noreferrer">
                利用規約
              </Link>
            </li>
            <li>
              <Link to="/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
                プライバシーポリシー
              </Link>
            </li>
            <li>
              <Link to="/legal/commerce-notation" target="_blank" rel="noopener noreferrer">
                特定商取引法に基づく表記
              </Link>
            </li>
            <li>
              <Link to="/legal/stripe-payment-info" target="_blank" rel="noopener noreferrer">
                Stripe情報
              </Link>
            </li>
            <li>
              <Link to="/legal/disclaimer" target="_blank" rel="noopener noreferrer">
                免責事項
              </Link>
            </li>
          </ul>
          <p>
            上記の規約および情報をご確認のうえ、お支払いください。
          </p>
        </div>
        <div className="cards-grid">
          {products.map((product, index) => (
            <div
              key={product.planId}
              className={`product-card ${product.isCustom ? 'full-width' : ''}`}
            >
              <h3>{product.price}</h3>
              {!product.isCustom ? (
                <p>{product.description}</p>
              ) : (
                <p>
                  {product.description.split('\n').map((line, i) => (
                    <React.Fragment key={`${product.planId}-${i}`}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </p>
              )}
              {product.isCustom && (
                <div className="custom-amount">
                  <label htmlFor={`custom-amount-${index}`}>金額（円）</label>
                  <input
                    id={`custom-amount-${index}`}
                    type="number"
                    min="500"
                    max="100000"
                    step="1"
                    value={customAmount}
                    onChange={(event) => setCustomAmount(event.target.value)}
                  />
                  {customAmountInvalid && (
                    <div className="custom-amount-error">500円～100000円の範囲で入力してください。</div>
                  )}
                </div>
              )}
              <div className="stripe-button-wrapper">
                <button
                  type="button"
                  className="stripe-checkout-button"
                  onClick={() => handleCheckout(product)}
                  disabled={
                    (isSubmitting && activePlanId === product.planId) ||
                    (product.isCustom && customAmountInvalid)
                  }
                >
                  {isSubmitting && activePlanId === product.planId ? '処理中...' : '支払う'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="bank-transfer-note">
          銀行振込をご利用の場合、着金確認後に支払い済み状態へ反映されます。
        </div>

        <section className="proxy-section">
          <div className="proxy-header">
            <h3>代理支払い登録</h3>
            <span className="proxy-pill">残り {availableUnits} 人分</span>
          </div>
          <p>
            支払い後に代理で支払ったユーザーを登録できます。1人分は500円です。
          </p>
          <div className="proxy-form">
            <label htmlFor="proxy-user-select">代理支払いするユーザー</label>
            <div className="proxy-input-row">
              <select
                id="proxy-user-select"
                value={selectedProxyUserId}
                onChange={(event) => setSelectedProxyUserId(event.target.value)}
              >
                <option value="">ユーザーを選択</option>
                {proxyUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.username}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="proxy-button"
                onClick={handleProxyRegister}
                disabled={isAllocating || availableUnits <= 0}
              >
                {isAllocating ? '登録中...' : '登録する'}
              </button>
            </div>
            {proxyUsersError && <div className="proxy-message error">{proxyUsersError}</div>}
            {proxyError && <div className="proxy-message error">{proxyError}</div>}
            {proxyMessage && <div className="proxy-message success">{proxyMessage}</div>}
          </div>
        </section>

      </div>
    </div>
  );
};

export default StripePaymentLink;
